import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { useRole } from '../../contexts/RoleContext'
import { theme } from '../../theme/theme'
import { AuthPage } from './AuthPage'

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const LoadingSpinner = styled.div`
  border: 3px solid ${({ theme }) => theme.colors.surface};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
`

const LoadingText = styled.div`
  margin-top: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.md};
`

interface ProtectedRouteProps {
  children: React.ReactNode
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, isPasswordRecovery } = useAuth()
  const { currentRole, loading: roleLoading } = useRole()
  const navigate = useNavigate()
  const location = useLocation()

  React.useEffect(() => {
    // If user is authenticated and on login page, redirect based on role
    if (user && !isPasswordRecovery && !roleLoading && currentRole && location.pathname === '/login') {
      if (currentRole === 'athlete') {
        navigate('/dashboard', { replace: true })
      } else if (currentRole === 'coach') {
        navigate('/athletes', { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    }
  }, [user, isPasswordRecovery, currentRole, roleLoading, navigate, location.pathname])

  if (loading || (user && roleLoading)) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Loading...</LoadingText>
      </LoadingContainer>
    )
  }

  if (!user || isPasswordRecovery) {
    console.log('No user found or password recovery mode, showing auth page')
    return <AuthPage />
  }

  return <>{children}</>
}
